import { Component, ErrorInfo, ReactNode } from 'react';
import { Sentry, isSentryEnabled } from '../lib/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error);
    console.error('Error info:', errorInfo);
    console.error('Component stack:', errorInfo.componentStack);

    // Send to Sentry if configured
    if (isSentryEnabled()) {
      Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    }

    this.setState({
      error,
      errorInfo
    });
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex h-screen w-full items-center justify-center bg-background p-6">
          <div className="w-full max-w-2xl rounded-lg border border-destructive bg-destructive/10 p-6">
            <h2 className="mb-4 text-xl font-bold text-destructive">
              Algo salió mal
            </h2>
            <div className="space-y-4">
              <div>
                <p className="font-semibold text-foreground">Error:</p>
                <pre className="mt-2 overflow-auto rounded bg-muted p-3 text-sm">
                  {this.state.error?.toString()}
                </pre>
              </div>
              {this.state.error?.stack && (
                <div>
                  <p className="font-semibold text-foreground">Stack trace:</p>
                  <pre className="mt-2 overflow-auto rounded bg-muted p-3 text-xs">
                    {this.state.error.stack}
                  </pre>
                </div>
              )}
              {this.state.errorInfo?.componentStack && (
                <div>
                  <p className="font-semibold text-foreground">Component stack:</p>
                  <pre className="mt-2 overflow-auto rounded bg-muted p-3 text-xs">
                    {this.state.errorInfo.componentStack}
                  </pre>
                </div>
              )}
              <button
                onClick={() => {
                  this.setState({ hasError: false, error: null, errorInfo: null });
                  window.location.reload();
                }}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
              >
                Recargar página
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
